import { createAction, createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import Router from 'next/router';
import { ITermsOfUse } from '../../../types/@types/generated/contentful';
import { UnverifiedUserProfile, UserProfile } from '../../../types/AuthTypes';
import { EVENTS, sendEvent, setUserProperties } from '../../analytics';
import { acceptTermsOfUse, getCurrentImpacterProfile } from '../../api';
import { getByUniqueSlug } from '../../api/contentful';
import { getSettings } from '../../api/settings';
import {
  getFirebase,
  handleAuthentication,
  loadFirebaseAuth,
  logout,
  refreshToken,
  setUserData,
} from '../../auth';
import { isApiError } from '../../error';
import { getToken } from '../../token';
import { captureException } from '../../tracking/sentry/sentry';
import { AuthModuleState } from './createModule';
import { getImpacterTermsMetaData, getUnverifiedUser } from './selectors';

enum actions {
  AUTHENTICATE = 'authentication/authenticate',
  AUTHENTICATE_GALAXY = 'authentication/authenticateWithGalaxy',
  RELOAD_UNVERIFIED_PROFILE = 'authentication/reloadUnverifiedProfile',
  SET_UNVERIFIED_USER = 'authentication/setUnverifiedUser',
  AUTHENTICATION_REJECTED = 'authentication/authenticationRejected',
  FETCH_USER_PROFILE = 'FETCH_USER_PROFILE',
  REFRESH = 'authentication/refresh',
  FETCH_IMPACTER_PROFILE = 'FETCH_IMPACTER_PROFILE',
  CLEAR_USER = 'CLEAR_USER',
  SET_UNVERIFIED_USER_ID = 'authentication/setUnverifiedUserId',
  FETCH_IMPACTER_TERMS = 'authentication/fetchImpacterTermsOfUseUpdated',
  ACCEPT_IMPACTER_TERMS = 'authentication/acceptImpacterTerms',
}

export const authenticate = createAsyncThunk<
  void,
  { forceTokenRefreshFromApp?: boolean } | void,
  Record<string, void>
>(actions.AUTHENTICATE, async ({ forceTokenRefreshFromApp } = {}, { dispatch }) => {
  await loadFirebaseAuth();
  const firebase = await getFirebase();
  if (Router.query?.['custom-token']) {
    try {
      const firebaseUser = await signInUserFromCustomToken({
        token: Router.query['custom-token'] as string,
      });
      dispatch(
        authenticateWithGalaxy({
          forceTokenRefresh: forceTokenRefreshFromApp,
          firebaseUser,
          shouldFetchUnverified: true,
        }),
      );
      return;
    } catch (error) {
      captureException(error, {
        tags: {
          authenticationType: 'custom token',
        },
      });
      // continue counting on cache
    }
  }

  await firebase.auth().onAuthStateChanged(async (firebaseUser) => {
    sendEvent(EVENTS.firebaseAuthStateChanged.name, {
      signedIn: !!firebaseUser,
    });
    dispatch(authenticateWithGalaxy({ firebaseUser: firebaseUser }));
  });
});

const signInUserFromCustomToken = async ({ token }: { token: string }) => {
  const firebase = await getFirebase();
  try {
    const signin = await firebase.auth().signInWithCustomToken(token);
    return signin.user;
  } catch (error) {
    captureException(error);
    return null;
  }
};

const authenticateWithGalaxy = createAsyncThunk<
  void,
  {
    firebaseUser: firebase.User | null;
    shouldFetchUnverified?: boolean;
    forceTokenRefresh?: boolean;
  },
  Record<string, void>
>(
  actions.AUTHENTICATE_GALAXY,
  async ({ firebaseUser, shouldFetchUnverified, forceTokenRefresh }, { dispatch }) => {
    if (!firebaseUser) {
      dispatch(clearUser());
      return;
    }

    if (firebaseUser.emailVerified || shouldFetchUnverified) {
      dispatch(fetchUser({ forceTokenRefresh }));
    } else if (!firebaseUser.email) {
      dispatch(authenticateRejected());
    } else {
      dispatch(
        setUnverifiedUser({
          email: firebaseUser.email,
          name: firebaseUser.displayName || '',
        }),
      );
    }
  },
);

export const reloadUnverifiedProfile = createAsyncThunk<void, void, { state: AuthModuleState }>(
  actions.RELOAD_UNVERIFIED_PROFILE,
  async (_, { dispatch, getState }) => {
    const state = getState();
    if (!getUnverifiedUser(state)) {
      return;
    }
    await loadFirebaseAuth();
    const user = await (await getFirebase()).auth().currentUser;
    if (!user || !user.email || !user.displayName) {
      return;
    }
    const { email, displayName: name } = user;
    dispatch(
      setUnverifiedUser({
        email,
        name,
      }),
    );
  },
);

export const setUnverifiedUser = createAction<
  Omit<UnverifiedUserProfile, 'isUnverified'>,
  actions.SET_UNVERIFIED_USER
>(actions.SET_UNVERIFIED_USER);

export const authenticateRejected = createAction(actions.AUTHENTICATION_REJECTED);

export const fetchUser = createAsyncThunk<
  UserProfile,
  { forceTokenRefresh?: boolean } | void,
  { rejectValue: { authenticationError?: boolean } }
>(actions.FETCH_USER_PROFILE, async ({ forceTokenRefresh } = {}, { dispatch, rejectWithValue }) => {
  if (forceTokenRefresh) {
    try {
      await refreshToken();
    } catch (error) {
      captureException(error);
    }
  }

  if (!getToken()) {
    try {
      await handleAuthentication(false);
    } catch (error) {
      captureException(error);
      await logout();
      return rejectWithValue({ authenticationError: true });
    }
  }

  const getUser = async () => {
    try {
      return await setUserData();
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (isApiError(error) && error.status === 401) {
        return unwrapResult(await dispatch(refresh()));
      }
      throw error;
    }
  };

  try {
    const user = await getUser();

    if (user.roles.IMPACTER === true) {
      await Promise.all([
        dispatch(fetchImpacterProfile()),
        dispatch(fetchImpacterTermsOfUseUpdated()),
      ]);
    }

    return user;
  } catch (error) {
    captureException(error);
    await logout();
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    if (error.authenticationError) return rejectWithValue(error);

    throw error;
  }
});

const refresh = createAsyncThunk<
  UserProfile,
  void,
  { rejectValue: { authenticationError?: boolean } }
>(actions.REFRESH, async (_, { rejectWithValue }) => {
  try {
    await handleAuthentication(false);
    const user = await setUserData();
    return user;
  } catch (error) {
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    if (isApiError(error) && error.status === 401) {
      return rejectWithValue({ authenticationError: true });
    }
    // @ts-expect-error FIXME: handle error types explicitly for improved error handling
    return rejectWithValue(error);
  }
});

export const fetchImpacterProfile = createAsyncThunk(actions.FETCH_IMPACTER_PROFILE, async () => {
  return await getCurrentImpacterProfile();
});

export const clearUser = createAction(actions.CLEAR_USER);

export const setUnverifiedUserId = createAction(
  actions.SET_UNVERIFIED_USER_ID,
  (id: number | string) => {
    setUserProperties({ id });
    return { payload: { id } };
  },
);

export const acceptImpacterTerms = createAsyncThunk<void, void, { state: AuthModuleState }>(
  actions.ACCEPT_IMPACTER_TERMS,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = await getState();
    const slug = getImpacterTermsMetaData(state).slug;
    try {
      if (slug) {
        await acceptTermsOfUse(slug);
        dispatch(fetchUser());
      } else {
        throw new Error('Impacter terms of use slug has not been set');
      }
    } catch (error) {
      captureException(error);
      rejectWithValue(error);
    }
  },
);

export const fetchImpacterTermsOfUseUpdated = createAsyncThunk(
  actions.FETCH_IMPACTER_TERMS,
  async (_, { rejectWithValue }) => {
    const { clientSettings } = await getSettings();
    const termsSlug = clientSettings.IMPACTER_TERMS_OF_USE_SLUG;
    try {
      if (!termsSlug) {
        throw new Error('Could not retrieve terms of use slug');
      }
      const terms = await getByUniqueSlug<ITermsOfUse>({
        slug: termsSlug,
        contentType: 'termsOfUse',
      });
      if (terms) {
        return {
          updated: terms.fields.dateUpdated,
          slug: termsSlug,
        };
      }
    } catch (error) {
      captureException(error);
      rejectWithValue(error);
    }
  },
);
